// Import the process polyfill
import './processPolyfill';

/**
 * WebSocket Service for real-time updates
 * Manages WebSocket connections for pumps and pools updates
 */

// Determine WebSocket URL based on environment
let wsBaseUrl = 'ws://localhost:8000';

if (process?.env?.REACT_APP_ENV === "prod") {
  wsBaseUrl = 'wss://uppopumppu.sarkain.fi';
} else if (process?.env?.REACT_APP_ENV === "dev") {
  wsBaseUrl = 'wss://uppopumppu.sarkain.fi';
} else if (process?.env?.REACT_APP_ENV === "jk_lenovo") {
  wsBaseUrl = 'ws://localhost:8000';
} else if (process?.env?.REACT_APP_ENV === "moltres") {
  wsBaseUrl = 'ws://127.0.0.1:8000';
} else if (process?.env?.REACT_APP_ENV === "hardcode") {
  wsBaseUrl = 'ws://127.0.0.1:8000';
}

class WebSocketService {
  constructor() {
    this.connections = new Map();
    this.messageHandlers = new Map();
    this.reconnectAttempts = new Map();
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000; // 3 seconds
    this.heartbeatInterval = 30000; // 30 seconds
    this.heartbeatTimers = new Map();
  }

  /**
   * Connect to a WebSocket endpoint
   * @param {string} endpoint - The WebSocket endpoint (e.g., 'pumps', 'pools')
   * @param {string} token - JWT authentication token
   * @param {function} onMessage - Callback for handling messages
   * @param {function} onConnect - Optional callback when connection is established
   * @param {function} onDisconnect - Optional callback when connection is lost
   */
  connect(endpoint, token, onMessage, onConnect = null, onDisconnect = null) {
    // Close existing connection if any
    if (this.connections.has(endpoint)) {
      this.disconnect(endpoint);
    }

    const url = `${wsBaseUrl}/ws/${endpoint}/?token=${token}`;
    console.log(`[WebSocket] Connecting to ${endpoint}`);

    try {
      const ws = new WebSocket(url);

      ws.onopen = () => {
        console.log(`[WebSocket] Connected to ${endpoint}`);
        this.reconnectAttempts.set(endpoint, 0);
        
        // Start heartbeat
        this.startHeartbeat(endpoint);
        
        if (onConnect) {
          onConnect();
        }
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log(`[WebSocket] Message from ${endpoint}:`, data);
          
          // Handle pong response
          if (data.type === 'pong') {
            return;
          }
          
          if (onMessage) {
            onMessage(data);
          }
        } catch (error) {
          console.error(`[WebSocket] Error parsing message from ${endpoint}:`, error);
        }
      };

      ws.onerror = (error) => {
        console.error(`[WebSocket] Error on ${endpoint}:`, error);
      };

      ws.onclose = (event) => {
        console.log(`[WebSocket] Disconnected from ${endpoint}`, event.code, event.reason);
        
        // Stop heartbeat
        this.stopHeartbeat(endpoint);
        
        // Remove from connections
        this.connections.delete(endpoint);
        
        if (onDisconnect) {
          onDisconnect();
        }

        // Attempt reconnection if not a normal closure
        if (event.code !== 1000 && event.code !== 1001) {
          this.attemptReconnect(endpoint, token, onMessage, onConnect, onDisconnect);
        }
      };

      this.connections.set(endpoint, ws);
      this.messageHandlers.set(endpoint, onMessage);
      
    } catch (error) {
      console.error(`[WebSocket] Failed to create connection to ${endpoint}:`, error);
    }
  }

  /**
   * Attempt to reconnect to a WebSocket endpoint
   */
  attemptReconnect(endpoint, token, onMessage, onConnect, onDisconnect) {
    const attempts = this.reconnectAttempts.get(endpoint) || 0;
    
    if (attempts >= this.maxReconnectAttempts) {
      console.error(`[WebSocket] Max reconnect attempts reached for ${endpoint}`);
      return;
    }

    this.reconnectAttempts.set(endpoint, attempts + 1);
    const delay = this.reconnectDelay * Math.pow(1.5, attempts); // Exponential backoff
    
    console.log(`[WebSocket] Reconnecting to ${endpoint} in ${delay}ms (attempt ${attempts + 1}/${this.maxReconnectAttempts})`);
    
    setTimeout(() => {
      this.connect(endpoint, token, onMessage, onConnect, onDisconnect);
    }, delay);
  }

  /**
   * Start sending periodic heartbeat messages
   */
  startHeartbeat(endpoint) {
    const timer = setInterval(() => {
      const ws = this.connections.get(endpoint);
      if (ws && ws.readyState === WebSocket.OPEN) {
        try {
          ws.send(JSON.stringify({ type: 'ping' }));
        } catch (error) {
          console.error(`[WebSocket] Error sending heartbeat to ${endpoint}:`, error);
        }
      }
    }, this.heartbeatInterval);
    
    this.heartbeatTimers.set(endpoint, timer);
  }

  /**
   * Stop sending heartbeat messages
   */
  stopHeartbeat(endpoint) {
    const timer = this.heartbeatTimers.get(endpoint);
    if (timer) {
      clearInterval(timer);
      this.heartbeatTimers.delete(endpoint);
    }
  }

  /**
   * Disconnect from a WebSocket endpoint
   * @param {string} endpoint - The WebSocket endpoint
   */
  disconnect(endpoint) {
    const ws = this.connections.get(endpoint);
    if (ws) {
      console.log(`[WebSocket] Manually disconnecting from ${endpoint}`);
      this.stopHeartbeat(endpoint);
      ws.close(1000, 'Client disconnect');
      this.connections.delete(endpoint);
      this.messageHandlers.delete(endpoint);
      this.reconnectAttempts.delete(endpoint);
    }
  }

  /**
   * Disconnect from all WebSocket endpoints
   */
  disconnectAll() {
    console.log('[WebSocket] Disconnecting all connections');
    for (const endpoint of this.connections.keys()) {
      this.disconnect(endpoint);
    }
  }

  /**
   * Check if connected to an endpoint
   * @param {string} endpoint - The WebSocket endpoint
   * @returns {boolean}
   */
  isConnected(endpoint) {
    const ws = this.connections.get(endpoint);
    return ws && ws.readyState === WebSocket.OPEN;
  }

  /**
   * Send a message to a WebSocket endpoint
   * @param {string} endpoint - The WebSocket endpoint
   * @param {object} message - The message to send
   */
  send(endpoint, message) {
    const ws = this.connections.get(endpoint);
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(message));
    } else {
      console.warn(`[WebSocket] Cannot send message, not connected to ${endpoint}`);
    }
  }
}

// Create singleton instance
const websocketService = new WebSocketService();

export default websocketService;

